import React, { useEffect, useState, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
interface SocialProofToastProps {
  onRegistration: () => void;
}
const NAME_BANK = [{
  name: 'Marcus T.',
  location: 'Charlotte, NC'
}, {
  name: 'Sarah J.',
  location: 'Austin, TX'
}, {
  name: 'David K.',
  location: 'Denver, CO'
}, {
  name: 'Priya S.',
  location: 'Atlanta, GA'
}, {
  name: 'James W.',
  location: 'Tampa, FL'
}, {
  name: 'Rachel M.',
  location: 'Phoenix, AZ'
}, {
  name: 'Carlos R.',
  location: 'Houston, TX'
}, {
  name: 'Emily B.',
  location: 'Nashville, TN'
}, {
  name: 'Michael D.',
  location: 'Dallas, TX'
}, {
  name: 'Jennifer L.',
  location: 'Raleigh, NC'
}, {
  name: 'Kevin O.',
  location: 'Chicago, IL'
}, {
  name: 'Ashley P.',
  location: 'Seattle, WA'
}, {
  name: 'Brandon H.',
  location: 'Columbus, OH'
}, {
  name: 'Tiffany C.',
  location: 'Miami, FL'
}, {
  name: 'Daniel F.',
  location: 'Salt Lake City, UT'
}, {
  name: 'Nicole A.',
  location: 'Indianapolis, IN'
}, {
  name: 'Christopher V.',
  location: 'Boise, ID'
}, {
  name: 'Megan G.',
  location: 'Minneapolis, MN'
}, {
  name: 'Tyler N.',
  location: 'Kansas City, MO'
}, {
  name: 'Olivia E.',
  location: 'Richmond, VA'
}, {
  name: 'Anthony Z.',
  location: 'San Antonio, TX'
}, {
  name: 'Lauren Y.',
  location: 'Portland, OR'
}, {
  name: 'Jordan U.',
  location: 'Greenville, SC'
}];
// Fisher-Yates shuffle
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}
export function SocialProofToast({
  onRegistration
}: SocialProofToastProps) {
  const [current, setCurrent] = useState<{
    name: string;
    location: string;
  } | null>(null);
  const queueRef = useRef<{
    name: string;
    location: string;
  }[]>(shuffle(NAME_BANK));
  const indexRef = useRef(0);
  useEffect(() => {
    const showNext = () => {
      const next = queueRef.current[indexRef.current % queueRef.current.length];
      indexRef.current += 1;
      setCurrent(next);
      onRegistration(); // bump the seats counter
      // Auto-dismiss
      window.setTimeout(() => setCurrent(null), 6000);
    };
    const t1 = window.setTimeout(showNext, 18000); // first toast within 20s
    const t2 = window.setTimeout(showNext, 45000); // second toast at 45s
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [onRegistration]);
  return <div className="fixed left-4 z-[60] pointer-events-none" style={{
    bottom: 'calc(env(safe-area-inset-bottom, 0px) + 96px)'
  }} data-id="element-145">
      {/* On larger screens, sit lower (no sticky bottom CTA bar) */}
      <style data-id="element-146">{`
        @media (min-width: 768px) {
          .social-proof-toast-wrap { bottom: 24px !important; }
        }
      `}</style>

      <div className="social-proof-toast-wrap" style={{
      position: 'absolute',
      bottom: 0,
      left: 0
    }} data-id="element-147">
        <AnimatePresence data-id="element-148">
          {current && <motion.div key={`${current.name}-${indexRef.current}`} initial={{
          opacity: 0,
          x: -32,
          y: 8
        }} animate={{
          opacity: 1,
          x: 0,
          y: 0
        }} exit={{
          opacity: 0,
          x: -32
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="pointer-events-auto bg-white rounded-xl shadow-2xl border border-gray-100 px-3.5 py-3 flex items-center gap-3 max-w-[280px] sm:max-w-xs" role="status" aria-live="polite" data-id="element-149">
              <div className="flex-shrink-0 w-9 h-9 rounded-full bg-brand-gold/15 flex items-center justify-center" data-id="element-150">
                <CheckCircle2 className="w-5 h-5 text-brand-gold" strokeWidth={2.5} data-id="element-151" />
              </div>
              <div className="min-w-0 flex-1" data-id="element-152">
                <p className="text-sm font-semibold text-gray-900 leading-tight truncate" data-id="element-153">
                  {current.name} just registered
                </p>
                <p className="text-[11px] text-gray-500 mt-0.5 truncate" data-id="element-154">
                  {current.location} · just now
                </p>
              </div>
              {/* Pulsing green live dot */}
              <span className="relative flex h-2 w-2 flex-shrink-0" data-id="element-155">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" data-id="element-156"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" data-id="element-157"></span>
              </span>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}